import React, { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  Twitter,
  Linkedin,
  Clock,
  Calendar,
  ArrowUp,
  Copy,
  Check,
} from "lucide-react";
import Footer from "../components/Footer";

const PAMBlog = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeSection, setActiveSection] = useState("");

  const contentRef = useRef(null);

  // State and Refs for controlling the FIXED sidebar behavior
  const [tocState, setTocState] = useState("normal"); // 'normal' | 'fixed' | 'bottom'
  const [sidebarWidth, setSidebarWidth] = useState(0);
  const mainGridRef = useRef(null);
  const sidebarContainerRef = useRef(null);

  // Exact data mapped perfectly to your 12 provided sections
  const articleSections = [
    {
      id: "section1",
      title: "Overview",
      content: `
        <p>Privileged access management (PAM) helps organizations securely administer access rights and permissions for privileged account users. PAM can help protect organizations from data leaks, attacks, and breaches. PAM can also mitigate the impact of a breach or attack should one happen.</p>
      `,
    },
    {
      id: "section2",
      title: "What are privileges?",
      content: `
        <p>Privileges are defined as the level of authority granted to an “identity” (a user or a machine) to enable access to data, systems, or devices. Privileges give the user distinct or elevated access that is different from what might be given to an average user.</p>
      `,
    },
    {
      id: "section3",
      title: "What is privileged access?",
      content: `
        <p>Privileged access is the unique or special access given to a user that goes above and beyond the access granted to a standard user. Organizations apply the principles of privileged access to secure their systems, data, applications, and infrastructure.</p>
      `,
    },
    {
      id: "section4",
      title: "Which types of privileged accounts exist?",
      content: `
        <p>Privileged accounts can range from user and domain admin accounts to service and application admin accounts, but they all play an important role in IT infrastructure. Each provides essential access for things like system maintenance and even emergency situations; all accounts, however, be they local admin, guest, or vendor accounts, can pose significant security risks due to their elevated privileges.</p>
        <p>That's where PAM tools come into play: they are vital when it comes to managing the types of accounts mentioned above and can ensure that access is always granted securely. Effective management of these accounts is key to maintaining robust security and operational integrity.</p>
        
        <h4 class="text-xl font-extrabold text-slate-800 mt-8 mb-2">User Accounts</h4>
        <p>Regular user accounts can be elevated to perform specific tasks, making them susceptible to misuse. They are typically used for daily operational activities but can be exploited if elevated privileges are not carefully controlled.</p>
        
        <h4 class="text-xl font-extrabold text-slate-800 mt-8 mb-2">Domain Administrator Accounts</h4>
        <p>These accounts have wide-ranging privileges across the entire network, allowing for tasks such as configuring systems, managing user rights, and setting security policies. Their broad access makes them a high-value target for cyber threats.</p>
        
        <h4 class="text-xl font-extrabold text-slate-800 mt-8 mb-2">Local Administrator Accounts</h4>
        <p>These provide administrative control over individual machines. They are essential for installing software and changing system settings but can pose a risk if compromised, as they allow complete control over the local system.</p>
        
        <h4 class="text-xl font-extrabold text-slate-800 mt-8 mb-2">Application Administrator Accounts</h4>
        <p>Used to manage individual applications, these accounts can access sensitive application data and change configurations. They need strict control to prevent unauthorized access to critical business applications.</p>
        
        <h4 class="text-xl font-extrabold text-slate-800 mt-8 mb-2">Service Accounts</h4>
        <p>These are specialized accounts that run services or applications, often having extensive system access. Mismanagement of these accounts can lead to significant security breaches, as they often have more privileges than necessary for their function.</p>
        
        <h4 class="text-xl font-extrabold text-slate-800 mt-8 mb-2">Guest Accounts</h4>
        <p>Typically offer limited access and are used for users requiring temporary access to resources. While their privileges are restricted, it's important to monitor and deactivate these accounts when not in use to prevent unauthorized access.</p>
        
        <h4 class="text-xl font-extrabold text-slate-800 mt-8 mb-2">System Accounts</h4>
        <p>Essential for the operation of the IT systems themselves, these accounts have extensive privileges and are used by the operating system to perform critical tasks. Compromise of these accounts can lead to severe system-level risks.</p>
        
        <h4 class="text-xl font-extrabold text-slate-800 mt-8 mb-2">Temporary or Elevated Privilege Accounts</h4>
        <p>Created for specific tasks requiring more privileges than available in a user's normal account. These accounts should be closely monitored and deactivated after the task is complete to avoid unnecessary security risks.</p>
        
        <h4 class="text-xl font-extrabold text-slate-800 mt-8 mb-2">Guest or Anonymous Accounts (in network contexts)</h4>
        <p>These accounts allow users to access network resources without identifying themselves. They generally have very limited privileges but can be exploited if not properly secured and monitored.</p>
        
        <h4 class="text-xl font-extrabold text-slate-800 mt-8 mb-2">Vendor or Third-Party Accounts</h4>
        <p>Provided to external parties for specific services or maintenance tasks. These accounts can be risky due to potential misuse and should be monitored for any unusual activity.</p>
        
        <h4 class="text-xl font-extrabold text-slate-800 mt-8 mb-2">Emergency or Break-Glass Accounts</h4>
        <p>Used in emergency situations, these accounts have very high-level access. They are critical for situations requiring immediate and broad access but pose a significant security risk if not controlled and monitored properly.</p>
      `,
    },
    {
      id: "section5",
      title:
        "What is the difference between privileged accounts and non-privileged accounts?",
      content: `
        <p>Most users at an organization qualify as non-privileged account users. These are users who are granted access to a limited number of systems or devices based on their role and function. Privileged account users typically have access levels beyond that of a non-privileged user. For example, a privileged account user may have “administrator” rights that grant them the ability to access sensitive systems, make network changes, install software, modify network settings and configurations, and control the access of other users. Sometimes privileged user accounts are referred to as superusers.</p>
        <p>Privileged accounts with elevated access rights pose a greater threat to security if these accounts are misused, abused, or stolen, making PAM an important best practice.</p>
      `,
    },
    {
      id: "section6",
      title: "What are the components of privileged access management?",
      content: `
        <p>Privileged access management includes:</p>
        <ul>
          <li>Granting and revoking access</li>
          <li>Ensuring those with privileged accounts have the right access to encourage user workflows and productivity</li>
          <li>Creating policies and expectations for intended use</li>
          <li>Behavior and user/identity activity monitoring</li>
          <li>Anomaly detection</li>
          <li>Session isolation</li>
          <li>Risk remediation</li>
        </ul>
      `,
    },
    {
      id: "section7",
      title:
        "What security risks are mitigated through privileged access management?",
      content: `
        <p>Privileged access management helps mitigate the risks associated with:</p>
        <ul>
          <li>Insider threats</li>
          <li>Accidental or unintended access</li>
          <li>Data leaks and breaches</li>
          <li>Cyberattacks involving stolen or compromised credentials</li>
          <li>Cryptolockers and ransomware</li>
          <li>Advanced Persistent Threats (APT)</li>
        </ul>
      `,
    },
    {
      id: "section8",
      title:
        "What is the difference between privileged access management and identity and access management (IAM)?",
      content: `
        <p>Privileged access management is part of the broader identity and access management lifecycle. PAM is typically viewed as the process by which organizations can control access on a more granular level.</p>
        
        <h4 class="font-bold text-slate-900 mt-8 mb-2">Scope and Purpose</h4>
        <p>As the PAM acronym suggests, the primary objective of privileged access management is to manage privileged accounts that have elevated access rights. PAM experts like our specialists at Synthera Security focus on controlling, monitoring, and securing access to your organization's critical resources. IAM encompasses a range of activities from user authentication and authorization to ensuring that access is appropriately aligned with each user's role and responsibilities.</p>
        
        <h4 class="font-bold text-slate-900 mt-8 mb-2">User Types</h4>
        <p>PAM and IAM target a range of user types within an organization.</p>
        <p>PAM specifically focuses on users with elevated access, such as system administrators, IT personnel, and executives, implementing stringent controls and monitoring for these high-risk accounts. This is a part of privileged identity management (PIM), ensuring that only authorized individuals have access to critical systems and data.</p>
        <p>IAM targets the broader user base, including regular employees, contractors, and third-party vendors, managing their access rights from entry-level to more privileged roles, ensuring each user has the appropriate level of access for their responsibilities.</p>
        
        <h4 class="font-bold text-slate-900 mt-8 mb-2">Access Control</h4>
        <p>PAM controls access at a granular level and focuses on the 'who, what, when, and where' of access. It provides detailed oversight of high-risk activities which includes limiting access to sensitive systems and implementing session monitoring for privileged users. IAM provides general user access based on roles and responsibilities and assigns permissions broadly, aligning with the user’s job function and organizational role.</p>
        
        <h4 class="font-bold text-slate-900 mt-8 mb-2">Use Cases</h4>
        <p>Privileged Access Management (PAM) and Identity and Access Management (IAM) are critical components in modern IT security and management. Here are a few specific situations where they are applied:</p>
        <ul>
          <li><strong>PAM in System Maintenance:</strong> In this scenario, an IT admin needs to perform system maintenance, so PAM is used to grant the admin temporary elevated access to critical systems or sensitive data.</li>
          <li><strong>PAM in Emergency Access:</strong> During emergencies such as system outages, PAM provides secure privileged access to resolve the issue, ensuring that only authorized personnel can access critical systems under strict oversight.</li>
          <li><strong>IAM in Employee Access to Company Files:</strong> This common IAM scenario involves employees accessing company files or applications based on their organizational role. IAM systems ensure that employees only have access to the information necessary for their job which keeps sensitive data protected from unnecessary exposure.</li>
          <li><strong>IAM in Customer Identity Management:</strong> In consumer-facing applications, IAM is used to manage customer identities. This involves authenticating customers and managing their access to services and personal data, often across multiple platforms and devices. This process enhances security while providing a seamless customer experience.</li>
        </ul>
        
        <h4 class="font-bold text-slate-900 mt-8 mb-2">Security Focus</h4>
        <p>PAM seeks to prevent the misuse of elevated privileges and ensure that high-level access, like in privileged sessions, may only be used in the way it was intended. IAM, meanwhile, ensures that the right people have the right access to corporate resources based on their roles and responsibilities. While PAM tightly controls and monitors access to sensitive systems, IAM establishes and manages user identities and permissions across an organization, providing secure, role-specific access. This dual approach strengthens overall security by managing both high-level and regular access rights.</p>
        
        <h4 class="font-bold text-slate-900 mt-8 mb-2">Implementation Complexity</h4>
        <p>PAM requires integration with various systems to control elevated access, a complexity that is compounded by the need to tailor PAM solutions to specific organizational structures and security policies. In contrast, IAM poses complexity due to its broad scope, catering to a diverse user base across different roles and departments. Our experts at Synthera Security can help you implement both PAM and IAM systems to improve your organizational security.</p>
        
        <h4 class="font-bold text-slate-900 mt-8 mb-2">Compliance and Audit</h4>
        <p>PAM emphasizes the criticality of monitoring privileged accounts to make sure that elevated access is both justified and closely tracked to prevent misuse. This focus on privileged accounts is crucial for complying with stringent regulations that demand meticulous oversight of high-level access. Meanwhile, IAM contributes by enforcing access controls and identity verification across the user base, aligning with regulatory requirements for data protection and access management.</p>
        
        <h4 class="font-bold text-slate-900 mt-8 mb-2">Integration with Other Security Tools</h4>
        <p>PAM and IAM integrate with other cybersecurity tools to enhance an organization's overall security posture; whereas PAM links with threat detection systems and leverages control over privileged accounts, IAM typically integrates with data protection solutions and leverages identity verification and access controls to keep sensitive data safe. PAM and IAM both act as foundational layers that enable synergistic operations to create a more robust and responsive defense against evolving cyber threats.</p>
      `,
    },
    {
      id: "section9",
      title: "What sets PAM and PIM apart?",
      content: `
        <p>PIM and PAM are at once distinct yet complementary from one another. PAM focuses on controlling and monitoring privileged account access; PIM centers on the identities associated with privileged accounts and involves managing the lifecycle of privileged identities. PIM ensures that only authorized personnel have privileged identities, emphasizing identity verification and management within the security framework.</p>

        <h4 class="font-bold text-slate-900 mt-8 mb-2">Scope and Objectives</h4>
        <p>PAM concerns itself with privileged account management to closely control access to critical systems. PAM focuses on the regulation of privileged accounts and often involves time-limited, closely monitored access. PIM, on the other hand, concerns itself with the management and monitoring of privileged identities, including the oversight of the lifecycle of privileged credentials to ensure that the appropriate personnel possess the appropriate access rights.</p>

        <h4 class="font-bold text-slate-900 mt-8 mb-2">User Focus</h4>
        <p>PAM focuses on the management of privileged accounts, controlling and monitoring access to critical systems and data. It's about the accounts themselves and their secure usage. Conversely, PIM centers on the users behind these accounts, specifically how identities are granted or inherit certain privileges. PIM manages who gets access and how that access aligns with their role and responsibilities, ensuring proper and secure distribution of privileged rights.</p>

        <h4 class="font-bold text-slate-900 mt-8 mb-2">Access Management</h4>
        <p>PAM stringently controls access to high-risk operations, ensuring that only authorized users can perform sensitive tasks. This includes monitoring for suspicious activity and restricting access to prevent unauthorized use of privileged accounts. PIM concentrates on the allocation of these privileges, determining which users receive access based on their roles and responsibilities. PIM focuses on the 'who' and 'why' behind privilege assignment, ensuring a secure and justified distribution of access rights.</p>

        <h4 class="font-bold text-slate-900 mt-8 mb-2">Application Scenarios</h4>
        <p><strong>Use of PAM:</strong></p>
        <ul>
          <li>When a system administrator requires temporary, elevated access for system maintenance or updates.</li>
          <li>During emergencies where immediate, controlled access to sensitive systems is necessary.</li>
          <li>To monitor and audit privileged sessions, ensuring they're used appropriately and for intended purposes.</li>
        </ul>
        <p><strong>Use of PIM:</strong></p>
        <ul>
          <li>To manage which individuals within an organization are granted privileged roles and access.</li>
          <li>For auditing and reviewing the distribution of privileged rights, ensuring they align with job functions.</li>
          <li>To track changes in role assignments, ensuring privileges are updated according to current responsibilities.</li>
        </ul>

        <h4 class="font-bold text-slate-900 mt-8 mb-2">Security Emphasis</h4>
        <p>PAM emphasizes securing the accounts themselves, implementing stringent controls, and monitoring access to these high-risk accounts. PIM focuses on the lifecycle and ongoing monitoring of privileged identities, ensuring that the allocation and management of these powerful roles are continually overseen and adjusted as your organizational needs evolve.</p>

        <h4 class="font-bold text-slate-900 mt-8 mb-2">Deployment Complexity</h4>
        <p>Deploying PAM involves intricately setting granular controls – it's a focused approach to high-risk accounts. On the other hand, PIM requires widespread implementation, managing a broader scope of user identities across an organization. PIM deals with the comprehensive lifecycle of privileged identities, from allocation to revocation, ensuring a secure and appropriate distribution of access rights.</p>

        <h4 class="font-bold text-slate-900 mt-8 mb-2">Compliance and Auditing</h4>
        <p>PIM and PAM are both integral to meeting privileged access management standards; PAM focuses on controlling and monitoring privileged accounts, a key aspect of adhering to stringent security regulations. PIM oversees the lifecycle and proper allocation of privileged identities, ensuring that access is granted in accordance with organizational policies and regulatory demands. Their combined efforts ensure comprehensive adherence to privileged access management standards.</p>

        <h4 class="font-bold text-slate-900 mt-8 mb-2">Integration with Additional Security Solutions</h4>
        <p>PAM and PIM play crucial roles in the larger cybersecurity landscape, integrating with various security solutions for a holistic approach. PAM, focusing on controlling and auditing privileged accounts, often integrates seamlessly with threat detection systems and security incident and event management tools. This integration allows for real-time monitoring and rapid response to any suspicious activities within privileged sessions.</p>
        <p>Conversely, PIM, which manages the lifecycle of privileged identities, complements this by ensuring that the right personnel are assigned appropriate access levels. It often integrates with identity governance and administration (IGA) systems, enhancing the process of identity verification and role-based access control.</p>
        <p>Together, PAM and PIM provide a comprehensive security strategy. While PAM ensures that access to critical systems is secure and monitored, PIM ensures that such access is appropriately assigned and managed. This dual approach not only tightens security against external threats but also provides robust safeguards against insider threats and potential misuse of privileged access within an organization. Their integration with broader cybersecurity solutions ensures a layered and effective defense, essential in today's complex security environment.</p>
      `,
    },
    {
      id: "section10",
      title: "What are the benefits of privileged access management?",
      content: `
        <p>Overall, privileged access management can minimize the possibility of a breach and reduce the extent of its impact should one occur. Other primary PAM benefits include:</p>
        <ul>
          <li>Reduced attack surface by controlling access to privileged accounts</li>
          <li>Minimized attack risk on a privileged account</li>
          <li>Controlled credential sharing</li>
          <li>Increased visibility and threat detection by tracking user behavior</li>
          <li>Improved compliance</li>
        </ul>
      `,
    },
    {
      id: "section11",
      title: "What are privileged access management best practices?",
      content: `
        <p>PAM best practices include:</p>
        <ul>
          <li>Examine and analyze the risks associated with all users that have or request privileged accounts.</li>
          <li>Regularly audit and maintain an inventory of all privileged accounts.</li>
          <li>Associate all privileged accounts with an owner and put a process in place to reassign privilege if the owner changes.</li>
          <li>Immediately remove privileged accounts for anyone who departs the organization.</li>
          <li>Monitor and log all activity on privileged accounts.</li>
          <li>Prohibit sharing of privileged account credentials.</li>
          <li>Minimize the number of privileged accounts to only those necessary.</li>
          <li>Enforce a strict privileged account password policy.</li>
          <li>Consider multi-factor authentication (MFA) mandatory for privileged accounts.</li>
          <li>Limit privileged account permissions to only those necessary or provide just-in-time access.</li>
          <li>Apply privileged elevation practices when someone needs to increase permissions.</li>
        </ul>
      `,
    },
    {
      id: "section12",
      title: "Implementing Privileged Access Management—Next Steps",
      content: `
        <p>Organizations wishing to implement PAM should start by determining what “privileged” means. For example, it could relate to sensitive information, infrastructure, critical business systems, or cloud environments. Once organizations have determined their definition of ‘privileged’ the next step is to identify privileged accounts for those areas using privileged access management solutions. A privileged access management assessment strategy can help an organization determine all relevant people, processes, and technologies associated with managing privileged accounts and determine the maturity level of the organization. Other implementation steps include:</p>
        <ul class="mb-8">
          <li>Building requirements</li>
          <li>Designing a proof of consent (POC)</li>
          <li>Setting up a password vault</li>
          <li>Integrating identity governance</li>
          <li>Integrating PAM with key infrastructure such as Active Directory</li>
        </ul>
        <p>Engage with a PAM professional to help you manage the privileged access management implementation process.</p>
        <p class="font-medium text-slate-700">Working with a PAM service provider can ensure your business achieves a more secure access environment that has the right structures and governance in place. Schedule a customized security consultation today with one of the Synthera Security experts to help you determine your security operations center needs.</p>
      `,
    },
  ];

  // Exactly 12 items mapping exactly to your text
  const tableOfContents = [
    { id: "section1", title: "Overview" },
    { id: "section2", title: "What are privileges?" },
    { id: "section3", title: "What is privileged access?" },
    { id: "section4", title: "Which types of privileged accounts exist?" },
    {
      id: "section5",
      title:
        "What is the difference between privileged accounts and non-privileged accounts?",
    },
    {
      id: "section6",
      title: "What are the components of privileged access management?",
    },
    {
      id: "section7",
      title:
        "What security risks are mitigated through privileged access management?",
    },
    {
      id: "section8",
      title:
        "What is the difference between privileged access management and identity and access management (IAM)?",
    },
    { id: "section9", title: "What sets PAM and PIM apart?" },
    {
      id: "section10",
      title: "What are the benefits of privileged access management?",
    },
    {
      id: "section11",
      title: "What are privileged access management best practices?",
    },
    {
      id: "section12",
      title: "Implementing Privileged Access Management—Next Steps",
    },
  ];

  // Custom Scroll Logic to force FIXED positioning once past the top section
  useEffect(() => {
    const handleScrollFixed = () => {
      if (!sidebarContainerRef.current || !mainGridRef.current) return;

      const containerRect = sidebarContainerRef.current.getBoundingClientRect();
      const gridRect = mainGridRef.current.getBoundingClientRect();

      const asideElement = sidebarContainerRef.current.querySelector("aside");
      const sidebarHeight = asideElement ? asideElement.offsetHeight : 500;

      const offsetTop = 128; // Represents top-32 placement

      // Force the width to perfectly match the grid column even when 'fixed' pulls it out of flow
      setSidebarWidth(sidebarContainerRef.current.offsetWidth);

      // Check if the article is finished (stops sidebar from hitting footer)
      if (gridRect.bottom <= offsetTop + sidebarHeight + 96) {
        setTocState("bottom");
      }
      // Check if we have scrolled past the top section -> Make it FIXED
      else if (containerRect.top <= offsetTop) {
        setTocState("fixed");
      }
      // Otherwise, it sits normally at the top
      else {
        setTocState("normal");
      }
    };

    window.addEventListener("scroll", handleScrollFixed);
    window.addEventListener("resize", handleScrollFixed);
    handleScrollFixed(); // Run once on load

    return () => {
      window.removeEventListener("scroll", handleScrollFixed);
      window.removeEventListener("resize", handleScrollFixed);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 500);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Intersection Observer to automatically highlight the active section in the TOC while scrolling
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-20% 0px -60% 0px" },
    );

    const sections = document.querySelectorAll("section[id]");
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <Helmet>
        <title>
          What Is Privileged Access Management (PAM)? | Synthera Security
        </title>

        <meta
          name="description"
          content="Learn what Privileged Access Management (PAM) is, how it works, PAM best practices, security benefits, and the differences between PAM, IAM, and PIM."
        />

        <meta
          name="keywords"
          content="PAM, Privileged Access Management, Identity Access Management, IAM, PIM, Cybersecurity, Privileged Accounts, Synthera Security"
        />

        <meta name="robots" content="index, follow" />

        <meta
          property="og:title"
          content="What Is Privileged Access Management (PAM)?"
        />

        <meta
          property="og:description"
          content="Understand PAM, privileged accounts, PAM best practices, and how organizations secure elevated access."
        />

        <meta
          property="og:url"
          content="https://www.syntherasecurity.com/blog/what-is-privileged-access-management-pam"
        />

        <meta property="og:type" content="article" />

        <link
          rel="canonical"
          href="https://www.syntherasecurity.com/blog/what-is-privileged-access-management-pam"
        />
      </Helmet>

      <div className="min-h-screen bg-white font-sans text-slate-800 relative">
        {/* Back to Top */}
        <button
          onClick={scrollToTop}
          className={`fixed bottom-8 right-8 z-50 p-4 bg-slate-900 text-white rounded-full shadow-2xl transition-all duration-500 hover:scale-110 hover:bg-[#00a684] ${
            showBackToTop
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-10 pointer-events-none"
          }`}
        >
          <ArrowUp className="w-6 h-6" />
        </button>

        {/* Main Container */}

        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-32 lg:pt-40">
          <div className="absolute top-0 left-0 right-0 h-20 bg-[#032d28] z-0  rounded-b-[1.5rem]"></div>
          {/* Header Section WITH IMAGE using grid layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-16">
            <div className="lg:col-span-7">
              <nav className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] mb-8">
                <Link to="/" className="text-[#00a684] no-underline">
                  Home
                </Link>
                <ChevronRight size={12} className="text-gray-300" />
                <Link to="/resources" className="text-[#00a684] no-underline">
                  Resources
                </Link>
                <ChevronRight size={12} className="text-gray-300" />
                <span className="text-[#00a684]">Blog</span>
              </nav>

              <div className="flex items-center gap-6 mb-6">
                <div className="flex items-center gap-2 text-xs font-semibold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100">
                  <Calendar size={14} /> June 4, 2026
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                  <Clock size={14} /> 10m read
                </div>
              </div>

              <h1 className="text-4xl lg:text-[44px] font-extrabold text-slate-900 leading-tight tracking-tight">
                What Is Privileged Access Management (PAM)?
              </h1>
              {/* <p className="mt-6 text-xl text-slate-600 leading-relaxed font-medium">
              Privileged access management (PAM) is the control and protection
              of elevated access rights for privileged accounts within an
              organization. It helps reduce the risk of data breaches and
              attacks by limiting, monitoring, and securing high-level access to
              critical systems and sensitive information.
            </p> */}

              {/* Custom Author Block */}
              <div className="flex items-center gap-2 mt-6">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center font-bold text-white shadow-lg text-sm">
                  SA
                </div>

                <div className="leading-tight">
                  <p className="text-base font-bold text-slate-800 mb-0">
                    Shoaib Arif
                  </p>

                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.12em] mt-0">
                    Technical Consulting Engineer
                  </p>
                </div>
              </div>
            </div>

            {/* Featured Image Restored Here */}
            <div className="lg:col-span-5">
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-[2rem] blur opacity-20 group-hover:opacity-40 transition duration-500"></div>
                <div className="relative overflow-hidden rounded-[2rem] shadow-2xl bg-slate-900 aspect-[4/3]">
                  <img
                    src="https://res.cloudinary.com/dpn3bkjba/image/upload/q_auto/f_auto/v1780572932/pam_nsczjo.png"
                    alt="Privileged Access Management"
                    className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Content Layout */}
          <div
            ref={mainGridRef}
            className="grid grid-cols-1 lg:grid-cols-12 gap-12 pb-24 items-start relative"
          >
            {/* FIXED LEFT SIDEBAR HANDLED BY JS */}
            <div
              ref={sidebarContainerRef}
              className="lg:col-span-3 hidden lg:block relative h-full"
            >
              <aside
                style={{
                  width: tocState !== "normal" ? `${sidebarWidth}px` : "auto",
                }}
                className={`
                ${tocState === "fixed" ? "fixed top-32" : ""}
                ${tocState === "bottom" ? "absolute bottom-24" : ""}
                ${tocState === "normal" ? "relative" : ""}
                space-y-8 max-h-[calc(100vh-10rem)] overflow-y-auto no-scrollbar
              `}
              >
                {/* Table of Contents UI block */}
                <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm mb-8">
                  <h4 className="text-[11px] font-black uppercase tracking-[0.3em] mb-6 text-slate-400 border-b pb-4">
                    Table of Contents
                  </h4>
                  <ul className="space-y-4">
                    {tableOfContents.map((item) => {
                      const isActive = activeSection === item.id;
                      return (
                        <li key={item.id} className="toc-item">
                          <a
                            href={`#${item.id}`}
                            style={{ textDecoration: "none" }}
                            className={`text-sm font-bold leading-snug block transition-all ${
                              isActive
                                ? "text-emerald-600 translate-x-1"
                                : "text-slate-500 hover:text-slate-800"
                            }`}
                          >
                            {item.title}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
                  <p className="text-[11px] font-black uppercase tracking-[0.3em] mb-4 text-slate-400">
                    Share Insight
                  </p>
                  <div className="flex gap-3">
                    <button className="flex-1 p-3 bg-slate-50 rounded-xl hover:bg-[#1DA1F2]/10 hover:text-[#1DA1F2] transition-all">
                      <Twitter size={18} className="mx-auto" />
                    </button>
                    <a
                      href="https://www.linkedin.com/feed/update/urn:li:activity:7456937237977522176"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 p-3 bg-slate-50 rounded-xl hover:bg-emerald-50 hover:text-emerald-600 transition-all"
                    >
                      <Linkedin size={18} className="mx-auto" />
                    </a>
                    <button
                      onClick={handleCopyLink}
                      className="flex-1 p-3 bg-slate-50 rounded-xl hover:bg-emerald-50 hover:text-emerald-600 transition-all"
                    >
                      {copied ? (
                        <Check size={18} className="mx-auto" />
                      ) : (
                        <Copy size={18} className="mx-auto" />
                      )}
                    </button>
                  </div>
                </div>
              </aside>
            </div>

            {/* MAIN ARTICLE AREA */}
            <div className="lg:col-span-9 max-w-3xl" ref={contentRef}>
              <div className="prose prose-lg max-w-none text-slate-600 leading-relaxed blog-content">
                {/* Intro Paragraph Added Directly Above Overview */}
                <p className="mt-6 text-xl text-slate-600 leading-relaxed font-medium mb-10">
                  Privileged access management (PAM) is the control and
                  protection of elevated access rights for privileged accounts
                  within an organization. It helps reduce the risk of data
                  breaches and attacks by limiting, monitoring, and securing
                  high-level access to critical systems and sensitive
                  information.
                </p>
                {/* Dynamic Content Sections */}
                {articleSections.map((section) => (
                  <section
                    key={section.id}
                    id={section.id}
                    className="mb-16 pt-8 border-t border-slate-100 first:border-0 first:pt-0"
                  >
                    <h2 className="text-3xl font-bold text-slate-800 m-0 mb-8 leading-tight">
                      {section.title}
                    </h2>
                    <div
                      dangerouslySetInnerHTML={{ __html: section.content }}
                    />
                  </section>
                ))}
              </div>
            </div>
          </div>
        </div>
        <Footer />
        <style>{`
        .blog-content h2 { margin-top: 0; color: #0f172a; font-weight: 800; }
        .blog-content p { margin-bottom: 1.75rem; font-size: 1.125rem; line-height: 1.85; }
        
        .blog-content section ul { list-style-type: none; padding-left: 0; margin-bottom: 2rem; }
        .blog-content section li { position: relative; padding-left: 1.75rem; margin-bottom: 0.75rem; font-weight: 500; }
        .blog-content section li::before {
          content: "•";
          color: #10b981;
          font-weight: bold;
          position: absolute;
          left: 0;
          font-size: 1.5rem;
          line-height: 1.2;
        }

        .toc-item::before, .dynamic-list li::before { display: none !important; }

        .highlight-box {
          background: #f0faf8;
          border-left: 4px solid #10b981;
          padding: 2rem;
          border-radius: 0 1.5rem 1.5rem 0;
          margin: 2.5rem 0;
          color: #064e3b;
        }
        .insight-box {
          background: #0f172a;
          color: white;
          padding: 2.5rem;
          border-radius: 1.5rem;
          margin: 3rem 0;
        }
        .insight-box h4 { color: #10b981; margin-bottom: 1rem; font-weight: 800; }
        html { scroll-behavior: smooth; }

        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
      </div>
    </>
  );
};

export default PAMBlog;
